import { z } from 'zod';
import { TextKey } from '@mews-framework/localization';
import { getFileExtensionsFromAccept } from '../../utils';
import { CustomErrorParams, CustomErrorType } from '../CustomErrorType';

interface BaseFileSchemaParams {
    sizeLimit: {
        bytes: number;
        text: string;
        perFile?: boolean;
    };
    allowMultiple?: boolean;
}

interface WithAcceptedTypes {
    acceptedTypes: string[];
}

const baseFileSchema = ({ sizeLimit, allowMultiple }: BaseFileSchemaParams) =>
    z
        .instanceof(File)
        .array()
        .refine((files) => allowMultiple || files.length <= 1, {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.MultipleFileDropNotAllowedErrorMessage,
            } satisfies CustomErrorParams,
        })
        .refine(
            (files) =>
                files.length === 0 || sizeLimit.perFile
                    ? files.every((file) => file.size <= sizeLimit.bytes)
                    : files.reduce((size, file) => size + file.size, 0) <= sizeLimit.bytes,
            {
                params: { errorType: CustomErrorType.FileSize, maxSize: sizeLimit.text } satisfies CustomErrorParams,
            }
        );

export const fileSchema = ({ sizeLimit, acceptedTypes, allowMultiple }: BaseFileSchemaParams & WithAcceptedTypes) =>
    baseFileSchema({ sizeLimit, allowMultiple }).refine(
        (files) => files.length === 0 || files.every((file) => acceptedTypes.includes(file.type)),
        {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.FilesAreNotSupportedErrorMessage,
                substitutes: {
                    supportedFileExtensions: getFileExtensionsFromAccept(acceptedTypes),
                },
            } satisfies CustomErrorParams,
        }
    );

export const imageFileSchema = (params: Omit<BaseFileSchemaParams, 'acceptedTypes' | 'allowMultiple'>) =>
    baseFileSchema(params).refine(
        (files) =>
            files.length === 0 || files.every((file) => ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)),
        {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.ConfigureProductDetailsProductImageFileTypeError,
            } satisfies CustomErrorParams,
        }
    );
